<template>
  <Transition name="slide-up">
    <div v-show="visible" class="sticky-add-to-bag">
      <Button width="full" @click="handleClick">
        {{ buttonText }}
      </Button>
    </div>
  </Transition>
</template>

<script>
import Button from "@/components/ButtonV3.vue";
import { mapState, mapGetters, mapActions } from "vuex";
import { getFeatureToggles } from "@/helpers/featureToggles.js";

export default {
  name: "StickyAddToBag",
  components: {
    Button,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["select-size"],
  data() {
    return {
      isVariantAddedToBag: false,
    };
  },
  computed: {
    ...mapState("product", ["selectedVariant", "product"]),
    ...mapGetters("product", [
      "variant",
      "preOrder",
      "sellingPlanAllocation",
      "availableForPreOrder",
      "selectedPrebuyCampaign",
      "prebuySellingPlanAllocation",
      "availableForPrebuy",
      "isPreLaunch",
    ]),
    availableForSale() {
      if (
        this.isPreLaunch ||
        !this.selectedVariant ||
        this.availableForPreOrder ||
        this.availableForPrebuy
      ) {
        return true;
      }

      return (
        this.selectedVariant?.quantityAvailable > 0 &&
        this.selectedVariant.availableForSale
      );
    },
    waitlistVersion() {
      const { waitlistVersion = 0 } = getFeatureToggles();
      return waitlistVersion;
    },
    buttonText() {
      if (this.isPreLaunch) return "COMING SOON - NOTIFY ME WHEN AVAILABLE";
      if (!this.selectedVariant) return "SELECT SIZE";
      if (this.isVariantAddedToBag) return "ADDED TO BAG";
      if (
        this.availableForSale &&
        (this.availableForPreOrder || this.availableForPrebuy)
      ) {
        return "PRE-ORDER";
      }
      if (this.selectedVariant && this.availableForSale) return "ADD TO BAG";
      if (this.selectedVariant && !this.availableForSale) {
        return this.waitlistVersion !== 0
          ? "JOIN THE WAITLIST"
          : "NOTIFY ME WHEN AVAILABLE";
      }
      return "";
    },
  },
  methods: {
    ...mapActions("cart", ["addCartItem", "setCartOpen"]),
    ...mapActions("add-to-bag", ["showCompactStockNotif", "openWaitlist"]),
    ...mapActions("product", ["setSelectedVariant"]),
    openStockNotification() {
      const variant = this.selectedVariant || this.variant;

      if (!variant) {
        return;
      }

      if (!this.selectedVariant) {
        this.setSelectedVariant(variant);
      }

      return this.showCompactStockNotif({
        product: this.product,
        variant,
        show: true,
      });
    },
    handleClick() {
      if (this.isPreLaunch) {
        this.openStockNotification();
        this.$emit("select-size");
        return;
      }

      if (!this.selectedVariant) {
        this.$emit("select-size");
        return;
      }

      if (
        !this.availableForSale &&
        !this.availableForPreOrder &&
        !this.availableForPrebuy
      ) {
        // Waitlist A/B test arms open the waitlist drawer instead of the
        // inline compact stock notification form.
        if (this.waitlistVersion !== 0) {
          this.openWaitlist();
          return;
        }

        this.openStockNotification();
        this.$emit("select-size");
        return;
      }

      this.isVariantAddedToBag = true;

      const usePrebuySystem = this.availableForPrebuy;
      const useOldPreorderSystem =
        this.availableForPreOrder && !usePrebuySystem;

      const attributes = [];
      let sellingPlanId = null;

      if (usePrebuySystem) {
        const campaign = this.selectedPrebuyCampaign;
        sellingPlanId = campaign?.sellingPlanId;

        if (campaign) {
          attributes.push(
            { key: "_locationId", value: campaign.locationId },
            { key: "_shipping", value: campaign.estDispatchDate },
            {
              key: "_sellingPlanName",
              value: this.prebuySellingPlanAllocation?.sellingPlan?.name || "",
            }
          );
        }
      } else if (useOldPreorderSystem) {
        sellingPlanId = this.preOrder.sellingPlanId;
        attributes.push({
          key: "_sellingPlanName",
          value: this.sellingPlanAllocation?.sellingPlan?.name,
        });
      }

      this.addCartItem({
        productId: this.product.id,
        variantId: this.selectedVariant.id,
        quantity: 1,
        ...(attributes.length > 0 && { attributes }),
        ...(sellingPlanId && { sellingPlanId }),
      });

      setTimeout(() => {
        this.isVariantAddedToBag = false;
      }, 1000);

      this.setCartOpen(true);
    },
  },
};
</script>

<style lang="scss" scoped>
.sticky-add-to-bag {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 12px 20px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
  z-index: 6;
  background: $color-white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.08);
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}
</style>
